import { buildEntry, classifyReference } from './helpers';
import { EnrichedReference } from './types';
import { flipSourceAndTarget } from '@ardoq/reference-operations';
import {
  APIReferenceAttributes,
  APIReferenceType,
  APIWorkspaceAttributes,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { flipReference } from 'streams/references/ReferenceActions';
import { groupBy, keyBy, partition } from 'lodash';
import { LabeledValue } from 'aqTypes';
import { t, type I18nLocale } from '@ardoq/i18n';
import { reverseReferencesModalOpened } from './reverseReferencesModalActions';

export type ResolvedReverseReference = {
  reference: APIReferenceAttributes;
  referenceTypeName: string | undefined;
  targetWsReferenceTypes: APIReferenceType[];
  sourceComponentName: string | null;
  targetComponentName: string | null;
  sourceWorkspaceName: string | null;
  targetWorkspaceData: APIWorkspaceAttributes | null;
};

const enrichReference = (
  resolved: ResolvedReverseReference,
  i18nLocale: I18nLocale
): EnrichedReference => {
  const {
    reference,
    referenceTypeName,
    targetWsReferenceTypes,
    sourceComponentName,
    targetComponentName,
    sourceWorkspaceName,
  } = resolved;
  const unselected: LabeledValue = {
    value: null,
    label: t(i18nLocale, 'reverseReferences:options.unselected', 'Unselected'),
  };
  const validOptions = [
    unselected,
    ...targetWsReferenceTypes.map(val => ({
      value: val.id,
      label: val.name,
    })),
  ];

  return {
    reference,
    referenceTypeName,
    sourceComponentName,
    targetComponentName,
    sourceWorkspaceName,
    validOptions,
  };
};

export const flipAndDispatch = (
  attributes: APIReferenceAttributes,
  wsFlipRefType: number,
  targetWorkspace: APIWorkspaceAttributes | null
): void => {
  if (!targetWorkspace) return;

  dispatchAction(
    flipReference(
      flipSourceAndTarget(attributes, targetWorkspace, wsFlipRefType)
    )
  );
};

export const classifyResolvedReference = (
  resolved: ResolvedReverseReference
) => {
  if (!resolved.reference.type) return null;
  if (resolved.targetWsReferenceTypes.length === 0) return null;
  return {
    resolved,
    classified: classifyReference(resolved.reference, {
      referenceTypeName: resolved.referenceTypeName,
      targetWsReferenceTypes: keyBy(resolved.targetWsReferenceTypes, 'id'),
    }),
  };
};

export const reverseResolvedReferences = (
  resolvedReferences: ResolvedReverseReference[],
  i18nLocale: I18nLocale
): void => {
  const classified = resolvedReferences
    .map(classifyResolvedReference)
    .filter(isTruthy);

  const [safeReferences, unsafeReferences] = partition(
    classified,
    ({ classified }) => classified.equivalentType !== null
  );

  safeReferences.forEach(({ resolved, classified }) => {
    flipAndDispatch(
      resolved.reference,
      classified.equivalentType!,
      resolved.targetWorkspaceData
    );
  });

  if (unsafeReferences.length === 0) return;

  const unsafeResolved = unsafeReferences.map(({ resolved }) => resolved);
  const enriched = unsafeResolved.map(resolved =>
    enrichReference(resolved, i18nLocale)
  );
  const grouped = groupBy(
    enriched,
    ({ reference, referenceTypeName }) =>
      `${reference.rootWorkspace}-${referenceTypeName}`
  );

  const entries = Object.values(grouped).map(buildEntry).filter(isTruthy);

  if (entries.length === 0) {
    return;
  }

  const unsafeResolvedById = keyBy(
    unsafeResolved,
    ({ reference }) => reference._id
  );

  dispatchAction(reverseReferencesModalOpened({ entries, unsafeResolvedById }));
};
